import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { SIZE } from '../../../../constants/size'
import { ScreenContainerRef } from '../../../../context'
import { getApi } from '../../../../utils/api'
import {
  EmailProfileMessageBody,
  EmailProfileMessageHeader,
  getEmailLinkedRequests,
} from '../../../Email'
import Modal, {
  Box,
  Content,
  Header,
  MINIMIZED_MODAL_KEY,
  ModalLoader,
  useMinimizableModal,
} from '../../../Modal'
import { getEntityEmailArchiveMessageApi } from '../EntityTabEmails.api'
import { useEntityTabEmailsActions } from '../EntityTabEmails.hooks'
import { getEntityTabEmailsMessageDeepLink } from '../EntityTabEmails.utils'

import { ENTITY_TAB_EMAILS_MESSAGE_MODAL_QA_NAME } from './EntityTabEmailsMessageModal.constants'
import { EntityTabEmailsMessageModalActions } from './EntityTabEmailsMessageModalActions'

import * as styles from './EntityTabEmailsMessageModal.module.css'

function EntityTabEmailsMessageModal({
  messageUuid,
  scopeId,
  onClose,
  onReload,
}) {
  const { pathname, search } = useLocation()

  const scrollWrapperRef = useRef(null)

  const [data, setData] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)

  // `isLoading` starts `true`, so the mount load doesn't need to flip it on
  // (which would be a synchronous set-state inside the effect). Only a reload
  // has to re-raise the loader, and it does so from its event handler below.
  async function handleLoad() {
    return getApi({
      endpoint: getEntityEmailArchiveMessageApi({ scopeId, uuid: messageUuid }),
    })
      .then(response => {
        setData(response.data)
      })
      .catch(() => {
        onClose()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (messageUuid && scopeId) {
      handleLoad()
    }
  }, [messageUuid, scopeId])

  function handleCloseNestedModal() {
    setModal(null)
  }

  async function handleReload() {
    setLoading(true)
    onReload?.()

    return handleLoad()
  }

  // The copy this modal shows is gone after "Delete copy", so reload only the
  // tab list and close instead of refetching the deleted message.
  function handleSuccessDeleteCopy() {
    onReload?.()
    onClose()
  }

  const { setItemAction } = useEntityTabEmailsActions({
    onCloseModal: handleCloseNestedModal,
    onReload: handleReload,
    onSetModal: setModal,
    onSuccessDeleteCopy: handleSuccessDeleteCopy,
  })

  // Default the fields the shared message body reads; the archive copy may
  // omit them.
  const messageData = data
    ? {
        ...data,
        archive_copy_entities: data.archive_copy_entities || EMPTY_OBJECT,
        files: data.files || EMPTY_LIST,
        linked_requests: getEmailLinkedRequests(data),
        permissions: data.permissions || EMPTY_OBJECT,
      }
    : null

  function handleClickAction({ value }) {
    setItemAction(value, messageData)
  }

  const { handleMinimize, isMinimized } = useMinimizableModal({
    minimizeKey: MINIMIZED_MODAL_KEY.ENTITY_TAB_EMAILS_MESSAGE,
    qaName: ENTITY_TAB_EMAILS_MESSAGE_MODAL_QA_NAME,
    title: data?.subject,
    onClose,
  })

  const openInNewWindowLink = getEntityTabEmailsMessageDeepLink({
    pathname,
    search,
    uuid: messageUuid,
  })

  return (
    <Modal
      hidden={isMinimized}
      qaName={ENTITY_TAB_EMAILS_MESSAGE_MODAL_QA_NAME}
      onClose={onClose}
    >
      <Box isFullScreen>
        <Header
          isFullScreen
          openInNewWindowLink={openInNewWindowLink}
          text={data?.subject}
          onClose={onClose}
          onMinimize={handleMinimize}
        />
        <ScreenContainerRef value={scrollWrapperRef}>
          <Content
            isFullScreen
            contentRef={scrollWrapperRef}
            size={SIZE.X_LARGE}
          >
            {messageData ? (
              <div className={styles.body}>
                <EmailProfileMessageHeader
                  Actions={EntityTabEmailsMessageModalActions}
                  actionsProps={{ onClickAction: handleClickAction }}
                  data={messageData}
                />
                <EmailProfileMessageBody data={messageData} />
              </div>
            ) : null}
          </Content>
        </ScreenContainerRef>
        <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
        {isLoading ? <ModalLoader /> : null}
      </Box>
    </Modal>
  )
}

export default EntityTabEmailsMessageModal
